<script setup lang="ts">
import type { ProductModelLookupItem } from '@/helpers/fetchers';
import type { BookBoxesProduct } from '@/models/pacBookBoxesJobBooking';

import KNumberStepperField from '@/components/fields/KNumberStepperField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import ProductModelLookup from '@/components/lookups/ProductModelLookup.vue';
import { createEmptyBookBoxesProduct } from '@/models/pacBookBoxesJobBooking';

export interface Props {
  isDeletable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isDeletable: false,
});

const emit = defineEmits<{
  deleteBtnClick: [event: Event];
}>();

const model = defineModel<BookBoxesProduct>({
  default: () => createEmptyBookBoxesProduct(),
});

function handleModelUpdate(value: string): void {
  model.value = {
    ...model.value,
    model: value || null,
    model_id: value ? model.value.model_id : null,
  };
}

function handleSelectItem(item: ProductModelLookupItem): void {
  model.value = { ...model.value, model: item.name, model_id: item.model_id };
}

function handleQuantity(value: number): void {
  model.value = { ...model.value, quantity: value };
}

function handleDescription(value: string | null | undefined): void {
  model.value = { ...model.value, description: value ?? '' };
}

function handleDeleteClick(event: Event): void {
  emit('deleteBtnClick', event);
}
</script>

<template>
  <div :class="$style.bookBoxesProductRow" data-testid="bookBoxesProductRow">
    <ProductModelLookup
      :model-value="model.model"
      label="Box type"
      size="md"
      @update:model-value="handleModelUpdate"
      @select-item="handleSelectItem"
    />
    <KNumberStepperField
      :model-value="model.quantity"
      :min="1"
      size="md"
      @update:model-value="handleQuantity"
    />
    <KTextField
      :model-value="model.description"
      label="Description"
      size="md"
      @update:model-value="handleDescription"
    />
    <v-btn
      v-if="props.isDeletable"
      icon="mdi-trash-can-outline"
      size="x-small"
      variant="text"
      color="error"
      aria-label="Delete product"
      :class="$style.deleteBtn"
      @click="handleDeleteClick"
    />
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.bookBoxesProductRow {
  display: grid;
  grid-template-columns: minmax(220px, 400px) auto minmax(160px, 1.4fr) auto;
  justify-content: start;
  gap: 16px;
  align-items: start;
  padding: 16px;
  border-bottom: 1px solid colors.$color-gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.deleteBtn {
  margin-top: 4px;
}
</style>
